import { UpgradeModel } from "types";

import aetherNexusImage from "assets/upgrade-aether-nexus.svg";
import bulwarkImage from "assets/upgrade-bulwark.svg";
import blazeReactorImage from "assets/upgrade-blaze-reactor.svg";
import energyBarrierImage from "assets/upgrade-energy-barrier.svg";
import faderGridImage from "assets/upgrade-fader-grid.svg";
import forceSanctuaryImage from "assets/upgrade-force-sanctuary.svg";
import glowReactorImage from "assets/upgrade-glow-reactor.svg";
import hyperburnerImage from "assets/upgrade-hyperburner.svg";
import jetsetterImage from "assets/upgrade-jetsetter.svg";
import lightLaserImage from "assets/upgrade-light-laser.svg";
import lightSentinelImage from "assets/upgrade-light-sentinel.svg";
import moonShotImage from "assets/upgrade-moon-shot.svg";
import needleGridImage from "assets/upgrade-needle-grid.svg";
import novaburnerImage from "assets/upgrade-novaburner.svg";
import photonSightImage from "assets/upgrade-photon-sight.svg";
import quantumBladeImage from "assets/upgrade-quantum-blade.svg";
import starBeamImage from "assets/upgrade-star-beam.svg";
import starBreakerImage from "assets/upgrade-star-breaker.svg";
import stellarSightImage from "assets/upgrade-stellar-sight.svg";
import veloNexusImage from "assets/upgrade-velo-nexus.svg";
import visionWallImage from "assets/upgrade-vision-wall.svg";
import voidDriveImage from "assets/upgrade-void-drive.svg";
import waveGridImage from "assets/upgrade-wave-grid.svg";

import {
  attackAnimation,
  reactorCoreAnimation,
  shieldsAnimation,
  targetingSystemAnimation,
  thrusterAnimation,
} from "components/styles/animations";
import {
  DEFAULT_REACTOR_SPEED,
  DEFAULT_SHIELDS_SPEED,
  DEFAULT_TARGETING_SPEED,
  DEFAULT_THRUSTERS_SPEED,
  DEFAULT_WEAPONS_SPEED,
} from "../constants";

const BISHOP_STRAYHOLD_UPGRADES: Record<string, UpgradeModel> = {
  // WEAPONS
  light_laser: {
    slug: "light_laser",
    name: "Light Laser",
    description:
      "A small laser upgrade that increases max damage and max weaken parts a little bit. Pew pew pew.",
    image: lightLaserImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 6,
    },
    grade: "basic",
    derivedStatsModifiers: {
      maxAttackDamage: 0.5,
      maxWeakenParts: 0.5,
    },
  },
  star_beam: {
    slug: "star_beam",
    name: "Star Beam",
    description: "A laser that decently weakens ship parts.",
    image: starBeamImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 6,
    },
    grade: "basic",
    derivedStatsModifiers: {
      maxAttackDamage: 1,
    },
  },
  moon_shot: {
    slug: "moon_shot",
    name: "Moon Shot",
    description: "A powerful laser that massively weakens ship parts.",
    image: moonShotImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 22,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxWeakenParts: 1,
    },
  },
  star_breaker: {
    slug: "star_breaker",
    name: "Star Breaker",
    description: "Uh... Try not to get hit by this.",
    image: starBreakerImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 24,
    },
    grade: "elite",
    derivedStatsModifiers: {
      maxAttackDamage: 1,
    },
  },
  quantum_blade: {
    slug: "quantum_blade",
    name: "Quantum Blade",
    description: "A laser that increases your attack power and accuracy.",
    image: quantumBladeImage,
    animation: attackAnimation,
    animationSpeed: DEFAULT_WEAPONS_SPEED,
    part: "weapons",
    requirements: {
      firepower: 10,
      precision: 5,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      maxAttackDamage: 0.5,
      maxWeakenParts: 0.5,
      minAttackDamage: 1,
    },
  },
  // SHIELDS
  energy_barrier: {
    slug: "energy_barrier",
    name: "Energy Barrier",
    description: "A force field that increases your ship health.",
    image: energyBarrierImage,
    animation: shieldsAnimation,
    animationSpeed: DEFAULT_SHIELDS_SPEED,
    part: "shields",
    requirements: {
      resilience: 8,
    },
    grade: "basic",
    derivedStatsModifiers: {
      maxHealth: 1,
    },
  },
  bulwark: {
    slug: "bulwark",
    name: "Bulwark",
    description:
      "A force field that deflects damage and protects your ship's parts.",
    image: bulwarkImage,
    animation: shieldsAnimation,
    animationSpeed: DEFAULT_SHIELDS_SPEED,
    part: "shields",
    requirements: {
      resilience: 5,
    },
    grade: "basic",
    derivedStatsModifiers: {
      damageReduction: 0.5,
      weakenPartsReduction: 0.5,
    },
  },
  vision_wall: {
    slug: "vision_wall",
    name: "Firewall",
    description:
      "A special force field that increases your ship health as well as your maximum attack damage.",
    image: visionWallImage,
    animation: shieldsAnimation,
    animationSpeed: 800,
    part: "shields",
    requirements: {
      resilience: 13,
      firepower: 7,
    },
    grade: "elite",
    derivedStatsModifiers: {
      maxHealth: 1,
      maxAttackDamage: 1,
    },
  },
  force_sanctuary: {
    slug: "force_sanctuary",
    name: "Force Sanctuary",
    description:
      "A strong force field that protects your ship parts from damage.",
    image: forceSanctuaryImage,
    animation: shieldsAnimation,
    animationSpeed: 600,
    part: "shields",
    requirements: {
      resilience: 15,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      weakenPartsReduction: 1,
    },
  },
  light_sentinel: {
    slug: "light_sentinel",
    name: "Light Sentinel",
    description: "A powerful force field that effortlessly deflects damage.",
    image: lightSentinelImage,
    animation: shieldsAnimation,
    animationSpeed: 600,
    part: "shields",
    requirements: {
      resilience: 16,
    },
    grade: "elite",
    derivedStatsModifiers: {
      damageReduction: 1,
    },
  },
  // THRUSTERS
  jetsetter: {
    slug: "jetsetter",
    name: "Jetsetter",
    description:
      "Thrusters that increase your ability to deal attacks more often.",
    image: jetsetterImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 4,
    },
    grade: "basic",
    derivedStatsModifiers: {
      attackSpeed: 1,
    },
  },
  jetflare: {
    slug: "jetflare",
    name: "Jetflare",
    description:
      "Thrusters that increase your ability to deal attacks more often.",
    image: jetsetterImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 9,
    },
    grade: "basic",
    derivedStatsModifiers: {
      attackSpeed: 0.5,
      attackEvasion: 0.5,
    },
  },
  hyperburner: {
    slug: "hyperburner",
    name: "Hyperburner",
    description:
      "Strong thrusters that allow you to parry high damage attacks.",
    image: hyperburnerImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 12,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      attackEvasion: 1,
    },
  },
  novaburner: {
    slug: "novaburner",
    name: "Novaburner",
    description:
      "Strong thrusters that increase the frequency of your attacks.",
    image: novaburnerImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 13,
    },
    grade: "elite",
    derivedStatsModifiers: {
      attackSpeed: 1,
    },
  },
  void_drive: {
    slug: "void_drive",
    name: "Void Drive",
    description:
      "Powerful thrusters that elevate the frequency of your attacks as well as your shielding ...somehow.",
    image: voidDriveImage,
    animation: thrusterAnimation,
    animationSpeed: DEFAULT_THRUSTERS_SPEED,
    part: "thrusters",
    requirements: {
      speed: 24,
      resilience: 12,
    },
    grade: "ultra",
    derivedStatsModifiers: {
      attackSpeed: 1,
      maxHealth: 0.5,
      damageReduction: 0.5,
    },
  },
  // TARGETING SYSTEMS
  fader_grid: {
    slug: "fader_grid",
    name: "Fader Grid",
    description:
      "A targeting system upgrade that increases your minimum attack damage.",
    image: faderGridImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 5,
    },
    grade: "basic",
    derivedStatsModifiers: {
      minAttackDamage: 1,
    },
  },
  wave_grid: {
    slug: "wave_grid",
    name: "Wave Grid",
    description:
      "A targeting system upgrade that increases your minimum attack damage and attack accuracy.",
    image: waveGridImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 7,
    },
    grade: "basic",
    derivedStatsModifiers: {
      attackAccuracy: 1,
    },
  },
  photon_sight: {
    slug: "photon_sight",
    name: "Photon Sight",
    description:
      "A special targeting system that increases your minimum attack damage as well as bumps up your antimatter boost.",
    image: photonSightImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 6,
      energy: 3,
    },
    grade: "basic",
    derivedStatsModifiers: {
      minAttackDamage: 1,
      energyMultiplier: 1,
    },
  },
  needle_grid: {
    slug: "needle_grid",
    name: "Needle Grid",
    description:
      "A robust targeting system upgrade increase your minimum attack damage.",
    image: needleGridImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 18,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      minAttackDamage: 1,
    },
  },
  stellar_sight: {
    slug: "stellar_sight",
    name: "Stellar Sight",
    description:
      "A powerful targeting system upgrade that helps you land attacks more consistently.",
    image: stellarSightImage,
    animation: targetingSystemAnimation,
    animationSpeed: DEFAULT_TARGETING_SPEED,
    part: "targetingSystem",
    requirements: {
      precision: 19,
    },
    grade: "elite",
    derivedStatsModifiers: {
      attackAccuracy: 1,
    },
  },
  // REACTOR CORES
  glow_reactor: {
    slug: "glow_reactor",
    name: "Glow Reactor",
    description:
      "An upgrade to your reactor core that strengthens your pilot abilities and skill frequency.",
    image: glowReactorImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 10,
    },
    grade: "basic",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
  },
  blaze_reactor: {
    slug: "blaze_reactor",
    name: "Blaze Reactor",
    description:
      "An upgrade to your reactor core that strengthens your pilot abilities and skill frequency.",
    image: blazeReactorImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 5,
    },
    grade: "basic",
    derivedStatsModifiers: {
      energyMultiplier: 0.5,
      fasterRecharge: 0.5,
    },
  },
  velo_nexus: {
    slug: "velo_nexus",
    name: "Velo Nexus",
    description:
      "A powerful upgrade to your reactor core that allows you to employ pilot abilities more often.",
    image: veloNexusImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 10,
    },
    grade: "advanced",
    derivedStatsModifiers: {
      fasterRecharge: 1,
    },
  },
  aether_nexus: {
    slug: "aether_nexus",
    name: "Aether Nexus",
    description:
      "A very powerful upgrade to your reactor core that boosts your pilot abilities handily.",
    image: aetherNexusImage,
    animation: reactorCoreAnimation,
    animationSpeed: DEFAULT_REACTOR_SPEED,
    part: "reactorCore",
    requirements: {
      energy: 11,
    },
    grade: "elite",
    derivedStatsModifiers: {
      energyMultiplier: 1,
    },
  },
};

export default BISHOP_STRAYHOLD_UPGRADES;
